// src/hooks/useSavedSchemas.ts

import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import toast from 'react-hot-toast';
import {
  saveSchema,
  getSchemas,
  updateSchema,
  deleteSchema
} from '../services/api';

export interface SavedSchema {
  id: number;
  name: string;
  description?: string;
  schemaJson?: string;
  inputType?: string;
  target: number;
  createdAt: string;
  updatedAt: string;
}

export interface SaveSchemaRequest {
  name: string;
  description?: string;
  schemaJson: string;
  target: number;
}

export const useSavedSchemas = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [schemas, setSchemas] =
    useState<SavedSchema[]>([]);
  const [loading, setLoading] = useState(false);

  const getToken = async () => {
    return await getAccessTokenSilently({
      authorizationParams: {
        audience: "https://api.scaffoldbridge.io"
      }
    });
  };

  const loadSchemas = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const data = await getSchemas(token);
      setSchemas(data);
    } catch (err) {
      console.error('Failed to load schemas:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);


  const save = async (
    request: SaveSchemaRequest
  ) => {
    try {
      const token = await getToken();
      const saved = await saveSchema(
        request, token);
      setSchemas(prev => [saved, ...prev]);
      toast.success('Schema saved! ✅');
      return saved;
    } catch (err) {
      toast.error('Failed to save schema');
      throw err;
    }
  };

  const update = async (
    id: number,
    request: SaveSchemaRequest
  ) => {
    try {
      const token = await getToken();
      const updated = await updateSchema(
        id, request, token);
      setSchemas(prev =>
        prev.map(s => s.id === id ? updated : s));
      toast.success('Schema updated! ✅');
      return updated;
    } catch (err) {
      toast.error('Failed to update schema');
      throw err;
    }
  };

  const remove = async (id: number) => {
    try {
      const token = await getToken();
      await deleteSchema(id, token);
      setSchemas(prev =>
        prev.filter(s => s.id !== id));
      toast.success('Schema deleted! ✅');
    } catch (err) {
      toast.error('Failed to delete schema');
      throw err;
    }
  };

  return {
    schemas,
    loading,
    loadSchemas,
    save,
    update,
    remove
  };
};